import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminSection } from "./admin";
import { AdminButton, AdminDialog, Field, Input, Row, TableShell, Textarea } from "@/components/admin/AdminUI";
import { ARTICLE_KINDS, ARTICLE_KIND_LABELS } from "@/lib/wisdom-kinds";

export const Route = createFileRoute("/_authenticated/admin/wisdom")({
  component: WisdomAdmin,
});

type W = Database["public"]["Tables"]["wisdom_posts"]["Row"];
type Ins = Database["public"]["Tables"]["wisdom_posts"]["Insert"];

const EMPTY: Ins = {
  slug: "", title: "", category: "teaching", reading_time: "", excerpt: "",
  pull_quote: "", body: [], tags: [], related_music: "", related_activation: "", related_book: "",
  published: false, published_at: null,
};


function WisdomAdmin() {
  const [rows, setRows] = useState<W[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<W | "new" | null>(null);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("wisdom_posts").select("*").order("created_at", { ascending: false });
    setRows(data ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function remove(id: string) {
    if (!confirm("Delete this wisdom post?")) return;
    const { error } = await supabase.from("wisdom_posts").delete().eq("id", id);
    if (error) alert(error.message); else load();
  }

  async function togglePublish(r: W) {
    const publish = !r.published;
    const { error } = await supabase.from("wisdom_posts").update({
      published: publish,
      published_at: publish ? new Date().toISOString() : null,
    }).eq("id", r.id);
    if (error) alert(error.message); else load();
  }

  return (
    <AdminSection
      title="Wisdom"
      description="Essays, teachings, and transmissions. Drafts are private; publish to release into the archive."
      action={<AdminButton onClick={() => setEditing("new")}>+ New post</AdminButton>}
    >
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
       rows.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> :
        <TableShell
          headers={["Title", "Category", "Status", "Published", ""]}
          rows={rows.map((r) => (
            <tr key={r.id} className="hover:bg-obsidian/40">
              <td className="px-4 py-3">
                <div className="font-serif text-base text-gold-luminous">{r.title}</div>
                <div className="text-xs text-muted-foreground">/{r.slug}</div>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{r.category}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                  r.published ? "text-emerald bg-emerald/10 border-emerald/30" : "text-muted-foreground border hairline"
                }`}>
                  {r.published ? "published" : "draft"}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">
                {r.published_at ? new Date(r.published_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <AdminButton variant="ghost" onClick={() => togglePublish(r)}>
                    {r.published ? "Unpublish" : "Publish"}
                  </AdminButton>
                  <AdminButton variant="ghost" onClick={() => setEditing(r)}>Edit</AdminButton>
                  <AdminButton variant="danger" onClick={() => remove(r.id)}>Delete</AdminButton>
                </div>
              </td>
            </tr>
          ))}
        />
      }
      {editing && <Form initial={editing === "new" ? EMPTY : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </AdminSection>
  );
}

/** Convert an ISO timestamp to the value shape a datetime-local input needs. */
const toLocalInput = (iso: string | null | undefined) =>
  iso ? new Date(iso).toISOString().slice(0, 16) : "";

function Form({ initial, onClose, onSaved }: { initial: W | Ins; onClose: () => void; onSaved: () => void }) {
  const isNew = !("id" in initial);
  const row = initial as Partial<W>;
  const [f, setF] = useState({
    slug: initial.slug ?? "",
    title: initial.title ?? "",
    category: initial.category ?? "teaching",
    reading_time: initial.reading_time ?? "",
    excerpt: initial.excerpt ?? "",
    pull_quote: initial.pull_quote ?? "",
    body: (initial.body ?? []).join("\n\n"),
    tags: ((initial as { tags?: string[] }).tags ?? []).join(", "),

    article_kind: row.article_kind ?? "evergreen",
    meta_title: row.meta_title ?? "",
    meta_description: row.meta_description ?? "",
    og_image_url: row.og_image_url ?? "",
    canonical_path: row.canonical_path ?? "",
    related_release_slug: row.related_release_slug ?? "",
    related_song_slug: row.related_song_slug ?? "",
    related_beat_slug: row.related_beat_slug ?? "",
    scheduled_at: toLocalInput(row.scheduled_at),

    related_music: initial.related_music ?? "",
    related_activation: initial.related_activation ?? "",
    related_book: initial.related_book ?? "",
    published: initial.published ?? false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    const existingPublished = "published" in initial ? initial.published : false;
    const existingAt = "published_at" in initial ? initial.published_at : null;
    const nn = (v: string) => v.trim() || null;
    const payload: Ins = {
      slug: f.slug.trim(), title: f.title.trim(), category: f.category,
      reading_time: f.reading_time.trim() || null,
      excerpt: f.excerpt, pull_quote: f.pull_quote || null,
      body: f.body.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean),
      tags: Array.from(
        new Set(f.tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean)),
      ),

      article_kind: f.article_kind,
      meta_title: nn(f.meta_title),
      meta_description: nn(f.meta_description),
      og_image_url: nn(f.og_image_url),
      canonical_path: nn(f.canonical_path),
      related_release_slug: nn(f.related_release_slug),
      related_song_slug: nn(f.related_song_slug),
      related_beat_slug: nn(f.related_beat_slug),
      // Scheduled posts stay drafts until publish_due_wisdom_posts() releases them.
      scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : null,

      related_music: nn(f.related_music),
      related_activation: nn(f.related_activation),
      related_book: nn(f.related_book),
      published: f.published,
      published_at: f.published
        ? (existingPublished && existingAt ? existingAt : new Date().toISOString())
        : null,
    };
    const q = isNew ? supabase.from("wisdom_posts").insert(payload) : supabase.from("wisdom_posts").update(payload).eq("id", (initial as W).id);
    const { error } = await q;
    setBusy(false);
    if (error) setError(error.message); else onSaved();
  }

  return (
    <AdminDialog open onClose={onClose} title={isNew ? "New wisdom post" : `Edit · ${initial.title}`}>
      <form onSubmit={submit} className="space-y-4">
        <Row>
          <Field label="Title"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Slug"><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Category"><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></Field>
          <Field label="Reading time"><Input value={f.reading_time} onChange={(e) => setF({ ...f, reading_time: e.target.value })} /></Field>
        </Row>
        <Field label="Article kind" hint="Drives the label shown in the reading room.">
          <select
            value={f.article_kind}
            onChange={(e) => setF({ ...f, article_kind: e.target.value })}
            className="w-full rounded-lg border hairline bg-obsidian px-3 py-2 text-sm"
          >
            {ARTICLE_KINDS.map((k) => (
              <option key={k} value={k}>{ARTICLE_KIND_LABELS[k]}</option>
            ))}
          </select>
        </Field>
        <Field label="Excerpt"><Textarea rows={2} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} /></Field>
        <Field label="Pull quote"><Textarea rows={2} value={f.pull_quote} onChange={(e) => setF({ ...f, pull_quote: e.target.value })} /></Field>
        <Field label="Body" hint="Separate paragraphs with a blank line">
          <Textarea rows={12} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
        </Field>
        <Field
          label="Topics / tags"
          hint="Comma separated, author-written. Shared topics connect this writing to books and Academy lessons. Leave empty when no genuine topic applies."
        >
          <Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="attention, kemetic wisdom" />
        </Field>

        <div className="border-t hairline pt-4">
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Search &amp; social</p>
        </div>
        <Row>
          <Field label="Meta title" hint="Under 60 characters. Falls back to the article title."><Input value={f.meta_title} onChange={(e) => setF({ ...f, meta_title: e.target.value })} /></Field>
          <Field label="Canonical path" hint="Only when this article canonicalises elsewhere."><Input value={f.canonical_path} onChange={(e) => setF({ ...f, canonical_path: e.target.value })} placeholder="/wisdom/slug" /></Field>
        </Row>
        <Field label="Meta description" hint="Under 160 characters. Falls back to the excerpt.">
          <Textarea rows={2} value={f.meta_description} onChange={(e) => setF({ ...f, meta_description: e.target.value })} />
        </Field>
        <Field label="Share image URL" hint="Absolute or site-relative. Used for Open Graph and Twitter previews.">
          <Input value={f.og_image_url} onChange={(e) => setF({ ...f, og_image_url: e.target.value })} />
        </Field>

        <div className="border-t hairline pt-4">
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Companion work</p>
        </div>
        <Row>
          <Field label="Related music (slug)"><Input value={f.related_music} onChange={(e) => setF({ ...f, related_music: e.target.value })} /></Field>
          <Field label="Related activation (slug)"><Input value={f.related_activation} onChange={(e) => setF({ ...f, related_activation: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Related release (slug)"><Input value={f.related_release_slug} onChange={(e) => setF({ ...f, related_release_slug: e.target.value })} /></Field>
          <Field label="Related song (slug)"><Input value={f.related_song_slug} onChange={(e) => setF({ ...f, related_song_slug: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Related book (slug)"><Input value={f.related_book} onChange={(e) => setF({ ...f, related_book: e.target.value })} /></Field>
          <Field label="Related beat (slug)"><Input value={f.related_beat_slug} onChange={(e) => setF({ ...f, related_beat_slug: e.target.value })} /></Field>
        </Row>

        <Field label="Schedule publish" hint="Leave empty to publish manually. Scheduled drafts go live automatically at this time.">
          <Input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} />
          <span>Published</span>
        </label>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t hairline pt-4">
          <AdminButton type="button" variant="ghost" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" disabled={busy}>{busy ? "Saving…" : isNew ? "Create" : "Save"}</AdminButton>
        </div>
      </form>
    </AdminDialog>
  );
}

